import React, { useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import { usePricingQuote } from '../../hooks/usePricingGuidance';
import { formatDateWithDay } from '../../utils/dateFormatter';
import { pricingErrorMessage } from '../../utils/pricingErrorCodes';
import ChannelEconomics from './ChannelEconomics';
import { moneyExact, count } from './pricingFormat';

/**
 * Block 3 — quote an exact stay. ONE block, one discount lever.
 *
 * This used to be a form, an eight-row bill, and a separate channel panel underneath.
 * Three of the bill's rows were reprinted verbatim in the panel's direct column
 * (`مجموع الغرفة` = `الإيراد قبل الضريبة`, and the same VAT and guest price), and the two
 * blocks each carried their own discount box — one measured off the ex-VAT room total
 * moving all three columns, the other off the VAT-inclusive guest price moving one. They
 * stacked silently: a 15% tier plus a 10% offer is 23.5% off the base, and no figure on
 * the screen said so. The bill is gone; what survived it is a one-line stay strip, and the
 * discount is a single lever inside the channel table (`ChannelEconomics`).
 *
 * ⚠️ The pre-filled dates come from the SERVER (`days[0].date`), never from the browser.
 * `new Date()` in a browser pre-fills YESTERDAY between midnight and 03:00 Riyadh, and
 * a quote box that silently starts in the past is worse than one that starts empty
 * (API_DATA_FETCHING_PATTERNS.md — `data.defaults`).
 */
const QuoteBox = ({ propertyId, defaultCheckIn, defaultCheckOut, floor }) => {
  const { t } = useLanguage();
  const [checkIn, setCheckIn] = useState(defaultCheckIn || '');
  const [checkOut, setCheckOut] = useState(defaultCheckOut || '');
  const [localError, setLocalError] = useState(null);

  const quote = usePricingQuote(propertyId);
  const result = quote.data;

  // The result survives edits to the form until the next submit. Saying so is the whole
  // guard: ops changed a date, read the table under it, and nothing marked the figures as
  // belonging to the previous range. Business-date strings compare directly.
  const stale = !!result && (checkIn !== result.checkIn || checkOut !== result.checkOut);

  const submit = (e) => {
    e.preventDefault();
    setLocalError(null);

    // A string comparison is safe and sufficient on ISO business dates, and avoids
    // parsing them into browser-local Date objects just to compare them.
    if (!checkIn || !checkOut || checkOut <= checkIn) {
      setLocalError(t('pricing.quote.invalidDates'));
      return;
    }

    quote.mutate({ checkIn, checkOut });
  };

  return (
    <section className="pg-block">
      <div className="pg-block__head">
        <h2 className="pg-block__title">
          <i className="bi bi-calculator" />
          {t('pricing.quote.heading')}
        </h2>
        <p className="pg-block__hint">{t('pricing.quote.hint')}</p>
      </div>

      <form className="pg-quote__form" onSubmit={submit}>
        <div className="pg-quote__field">
          <label htmlFor="pg-checkin">{t('pricing.quote.checkIn')}</label>
          <input
            id="pg-checkin"
            type="date"
            className="form-control"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </div>
        <div className="pg-quote__field">
          <label htmlFor="pg-checkout">{t('pricing.quote.checkOut')}</label>
          <input
            id="pg-checkout"
            type="date"
            className="form-control"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={quote.isPending}>
          {quote.isPending ? t('pricing.quote.submitting') : t('pricing.quote.submit')}
        </button>
      </form>

      {localError && (
        <div className="pg-notice pg-notice--warn">{localError}</div>
      )}

      {quote.isError && !localError && (
        <div className="pg-notice pg-notice--blocked">
          {pricingErrorMessage(quote.error, t, 'pricing.quote.error')}
        </div>
      )}

      {result && (
        <div className={`pg-quote__result${stale ? ' pg-quote__result--stale' : ''}`}>
          {/* The dates the figures below belong to. */}
          <div className="pg-quote__for-dates">
            {t('pricing.quote.forDates')} {formatDateWithDay(result.checkIn)}
            {' → '}
            {formatDateWithDay(result.checkOut)}
          </div>

          {stale && (
            <div className="pg-notice pg-notice--warn">{t('pricing.quote.stale')}</div>
          )}

          {/* An incomplete quote shows NO money. Every derived figure is null on the wire
              precisely so none of them can be rendered as a confident number for a stay we
              are unable to price. */}
          {!result.complete ? (
            <div className="pg-notice pg-notice--blocked">
              <div className="pg-notice__title">{t('pricing.quote.incomplete')}</div>
              {count(result.missingNights)} {t('pricing.common.nights')}{' '}
              {t('pricing.quote.incompleteHint')}
              <div style={{ marginTop: 6, fontSize: '0.78rem' }}>
                {result.perNight
                  // Same test the backend counts by: a night stored at 0 is unpriced, and
                  // filtering on `typeof !== 'number'` skipped it — the banner said one
                  // night was unpriced and then listed none.
                  .filter(n => !(typeof n.rate === 'number' && n.rate > 0))
                  .map(n => formatDateWithDay(n.date))
                  .join(t('pricing.common.listSeparator'))}
              </div>
            </div>
          ) : (
            /* The stay, in one line. Everything here is context for the table below it —
               what the discount comes off, and what the calendar happens to be asking for
               the same nights. The money itself is the table's job, and printing any of it
               twice is what this rewrite removes. */
            <div className="pg-quote__stay">
              <span>
                <span className="pg-quote__stay-k">{t('pricing.quote.nights')}</span>{' '}
                <b>{count(result.nights)}</b>
              </span>
              <span>
                <span className="pg-quote__stay-k">{t('pricing.quote.basisTotal')}</span>{' '}
                <b>{moneyExact(result.basisTotal)}</b> {t('pricing.common.currency')}
                {' ('}
                <b>{moneyExact(result.basisTotal / result.nights)}</b>
                {' / '}{t('pricing.common.perNight')})
                <span className="pg-footing"> {t('pricing.common.exVat')}</span>
              </span>
              {/* A FACT beside the quote, never the basis for it. The unit's published rate
                  can be a channel tactic — raised to make a promo land somewhere, lowered
                  to hold an experiment level — and reading it as the price is what sent a
                  310 quote out on a unit selling at 248. Shown only when it differs, so an
                  ordinary unit gains nothing. */}
              {result.basis === 'reference'
                && result.publishedNightly !== null
                && result.publishedNightly !== result.referenceRate && (
                <span>
                  <span className="pg-quote__stay-k">{t('pricing.quote.publishedNow')}</span>{' '}
                  <b>{moneyExact(result.publishedNightly)}</b> {t('pricing.common.currency')}
                  {' / '}{t('pricing.common.perNight')}
                  <span className="pg-footing"> {t('pricing.common.exVat')}</span>
                </span>
              )}
            </div>
          )}

          {/* Availability, whatever the money says. A stay that overlaps a sold or stopped
              night cannot be taken as quoted, and nothing else on the page would say so. */}
          {result.soldNights > 0 && (
            <div className="pg-notice pg-notice--blocked">
              {count(result.soldNights)} {t('pricing.common.nights')}{' '}
              {t('pricing.quote.soldWarning')}
            </div>
          )}
          {result.heldNights > 0 && (
            <div className="pg-notice pg-notice--blocked">
              {count(result.heldNights)} {t('pricing.common.nights')}{' '}
              {t('pricing.quote.heldWarning')}
            </div>
          )}
          {result.stopSoldNights > 0 && (
            <div className="pg-notice pg-notice--warn">
              {count(result.stopSoldNights)} {t('pricing.common.nights')}{' '}
              {t('pricing.quote.stopSoldWarning')}
            </div>
          )}

          {/* ⚠️ The floor warning lives in ChannelEconomics ALONE, and deliberately: with
              one lever the quoted price and the offered price are the same number, so two
              floor notices would be the same warning printed twice — and the one that
              matters is the one measured against what ops is actually about to offer. */}
          {result.economics && (
            <ChannelEconomics
              // Remounts per quote. The lever and the two price boxes hold real values,
              // not placeholders — without this key a new stay on the same unit would keep
              // the previous stay's total sitting in a box beside the new stay's table.
              key={`${result.checkIn}-${result.checkOut}-${result.basisTotal}`}
              economics={result.economics}
              nights={result.nights}
              floor={floor}
              basisTotal={result.basisTotal}
              tierDiscountPercent={result.tierDiscountPercent}
              longStay={result.longStay}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default QuoteBox;
